import React, { useState, Fragment, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
import { ArrowLeft, FileSearch, Save, Cpu, RefreshCw, FileText, MapPin, CheckCircle, FileImage, FileAudio, FileVideo, Send, Eye, CheckSquare, GitCompare, StickyNote, Target, Radio, Gauge, User, ShieldCheck, Globe, X, Play, XCircle, CornerUpLeft, Highlighter, PlusCircle, Trash2 } from 'lucide-react';
import { useStore, AiIntelReport } from '../data/store';
import { toast } from 'sonner';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { StatusBadge } from '../components/StatusBadge';
import { generateAiReport } from '../utils/aiGenerator';
import { IntelReportPreview } from '../components/IntelReportPreview';
import { Role } from '../data/rbac';
interface Revision {
  ts: string;
  label: string;
  data: AiIntelReport;
}
export default function ReportAnalysis() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const role = localStorage.getItem('bimcs_role') as Role || 'Super Admin Nasional';
  const reports = useStore(s => s.reports);
  const saveAiReport = useStore(s => s.saveAiReport);
  const verifyReport = useStore(s => s.verifyReport);
  const approveBinda = useStore(s => s.approveBinda);
  const nationalAccept = useStore(s => s.nationalAccept);
  const nationalRevise = useStore(s => s.nationalRevise);
  const rejectReport = useStore(s => s.rejectReport);
  const report = reports.find(r => r.id === id);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiReportData, setAiReportData] = useState<AiIntelReport | null>(report?.aiReport || null);
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [previewAtt, setPreviewAtt] = useState<{
    name: string;
    kind: string;
  } | null>(null);
  const [nationalOpen, setNationalOpen] = useState(false);
  const [execSummary, setExecSummary] = useState('');
  const [nationalNote, setNationalNote] = useState('');
  const [reviseReason, setReviseReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviseOpen, setReviseOpen] = useState(false);
  // Which editable section the analyst is currently working in. The editor
  // toolbar (Tambah / Hapus Paragraf, Highlight) operates on this field so the
  // buttons act on real content instead of being no-ops.
  const EDITABLE_FIELDS = ['facts', 'assessment', 'potentialImpact', 'earlyWarning', 'recommendations'] as const;
  type EditableField = (typeof EDITABLE_FIELDS)[number];
  const FIELD_LABEL: Record<EditableField, string> = {
    facts: 'I. Fakta-Fakta',
    assessment: 'II. Analisis',
    potentialImpact: 'III. Potensi Dampak',
    earlyWarning: 'IV. Perkiraan Perkembangan',
    recommendations: 'V. Rekomendasi'
  };
  const [focusedField, setFocusedField] = useState<EditableField>('facts');
  if (!report) {
    return <div className="flex flex-col items-center justify-center h-full space-y-4" data-id="element-3144">
        <h2 className="text-xl font-mono text-muted-foreground" data-id="element-3145">
          Laporan tidak ditemukan
        </h2>
        <Button onClick={() => navigate('/reports')} variant="outline" data-id="element-3146">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-3147" /> Kembali ke Daftar
        </Button>
      </div>;
  }
  const handleGenerateFull = () => {
    setIsGenerating(true);
    setTimeout(() => {
      const generated = generateAiReport(report);
      setAiReportData(generated);
      setIsGenerating(false);
      toast.success('Laporan Intelijen berhasil di-generate secara otomatis.');
    }, 1500);
  };
  const handleGenerateSection = (section: string) => {
    if (!aiReportData) {
      handleGenerateFull();
      return;
    }
    setIsGenerating(true);
    setTimeout(() => {
      const generated = generateAiReport(report);
      let updated = {
        ...aiReportData
      };
      switch (section) {
        case 'fakta':
          updated.facts = generated.facts;
          break;
        case 'analisis':
          updated.assessment = generated.assessment;
          break;
        case 'dampak':
          updated.potentialImpact = generated.potentialImpact;
          break;
        case 'perkembangan':
          updated.earlyWarning = generated.earlyWarning;
          break;
        case 'rekomendasi':
          updated.recommendations = generated.recommendations;
          break;
        case 'informasi':
          updated.facts = generated.facts;
          updated.assessment = generated.assessment;
          break;
        case 'executive':
          updated.situationSummary = generated.situationSummary;
          break;
      }
      setAiReportData(updated);
      setIsGenerating(false);
      toast.success(`Bagian ${section} berhasil di-generate ulang.`);
    }, 1000);
  };
  const handleReportChange = (field: keyof AiIntelReport, value: any) => {
    if (aiReportData) {
      setAiReportData({
        ...aiReportData,
        [field]: value
      });
    }
  };
  const pushRevision = (label: string, data: AiIntelReport) => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    setRevisions(prev => [{
      ts,
      label,
      data: {
        ...data
      }
    }, ...prev].slice(0, 12));
  };
  const handleSaveDraft = () => {
    if (aiReportData) {
      saveAiReport(report.id, aiReportData, false);
      pushRevision(`Revisi #${revisions.length + 1}`, aiReportData);
      toast.success('Draft tersimpan & snapshot revisi dicatat.');
    }
  };
  const handleAddNote = () => {
    if (!aiReportData || !noteText.trim()) {
      toast.error('Catatan tidak boleh kosong.');
      return;
    }
    const updated = {
      ...aiReportData,
      assessment: `${aiReportData.assessment}\n\n[CATATAN ANALIS]: ${noteText.trim()}`
    };
    setAiReportData(updated);
    setNoteText('');
    setNoteOpen(false);
    toast.success('Catatan analis ditambahkan ke bagian Analisis.');
  };
  // Read the currently-focused editable section as a plain string.
  const readField = (data: AiIntelReport, field: EditableField): string => {
    const v = data[field];
    return Array.isArray(v) ? v.join('\n\n') : v as string || '';
  };
  // Write a string back into the focused field, preserving earlyWarning's array shape.
  const writeField = (data: AiIntelReport, field: EditableField, value: string): AiIntelReport => {
    if (field === 'earlyWarning') {
      return {
        ...data,
        earlyWarning: value.split('\n\n').map(s => s.trim()).filter(Boolean)
      };
    }
    return {
      ...data,
      [field]: value
    };
  };
  const handleAddParagraph = () => {
    if (!aiReportData) return;
    const current = readField(aiReportData, focusedField);
    const next = (current ? current + '\n\n' : '') + '[Paragraf baru — sunting di sini]';
    setAiReportData(writeField(aiReportData, focusedField, next));
    toast.success(`Paragraf ditambahkan pada ${FIELD_LABEL[focusedField]}.`);
  };
  const handleDeleteParagraph = () => {
    if (!aiReportData) return;
    const current = readField(aiReportData, focusedField);
    const paras = current.split('\n\n').filter(p => p.trim().length > 0);
    if (paras.length <= 1) {
      toast.error('Minimal satu paragraf harus tersisa.');
      return;
    }
    paras.pop();
    setAiReportData(writeField(aiReportData, focusedField, paras.join('\n\n')));
    toast.success(`Paragraf terakhir dihapus dari ${FIELD_LABEL[focusedField]}.`);
  };
  // Highlight wraps the focused section's first un-highlighted paragraph in ==..==
  // markers; the preview renders these as a marked/highlighted span.
  const handleHighlight = () => {
    if (!aiReportData) return;
    const current = readField(aiReportData, focusedField);
    if (!current.trim()) {
      toast.error('Tidak ada teks untuk di-highlight.');
      return;
    }
    const paras = current.split('\n\n');
    const idx = paras.findIndex(p => p.trim() && !/^==[\s\S]*==$/.test(p.trim()));
    if (idx === -1) {
      toast.message('Semua paragraf sudah di-highlight.');
      return;
    }
    paras[idx] = `==${paras[idx].trim()}==`;
    setAiReportData(writeField(aiReportData, focusedField, paras.join('\n\n')));
    toast.success(`Teks di-highlight pada ${FIELD_LABEL[focusedField]}.`);
  };
  // TAHAP 1: Analis BINDA
  const handleVerifyBinda = () => {
    if (aiReportData) {
      saveAiReport(report.id, aiReportData, false);
      verifyReport(report.id, 'Telah diverifikasi oleh Analis BINDA.');
      toast.success('Laporan diverifikasi. Menunggu persetujuan Kabinda.');
    }
  };
  // TAHAP 2: Kabinda
  const handleApproveKabinda = () => {
    if (aiReportData) {
      saveAiReport(report.id, aiReportData, false);
      approveBinda(report.id, 'Disetujui oleh Kabinda untuk diteruskan ke Nasional.');
      toast.success('Laporan disetujui Kabinda dan dikirim ke Analis Nasional.');
    }
  };
  // TAHAP 3: Analis Nasional
  const handleNationalAccept = () => {
    if (!execSummary.trim()) {
      toast.error('Executive Summary wajib diisi.');
      return;
    }
    nationalAccept(report.id, {
      executiveSummary: execSummary,
      nationalAssessment: nationalNote,
      strategicAnalysis: aiReportData?.assessment || '',
      nationalRiskAssessment: aiReportData?.riskAnalysis || '',
      executiveNotes: nationalNote
    });
    setNationalOpen(false);
    toast.success('Laporan DITERIMA NASIONAL — resmi menjadi Produk Intelijen.');
  };
  const handleNationalRevise = () => {
    if (!reviseReason.trim()) {
      toast.error('Alasan revisi wajib diisi.');
      return;
    }
    nationalRevise(report.id, reviseReason);
    setNationalOpen(false);
    toast.success('Laporan dikembalikan ke BINDA untuk revisi.');
  };
  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Alasan penolakan wajib diisi.');
      return;
    }
    rejectReport(report.id, rejectReason);
    setRejectOpen(false);
    setRejectReason('');
    toast.success('Laporan ditolak.');
  };
  // Return for Revision — distinct from Reject. Sends the report back for
  // revision (stage "Perlu Revisi") rather than terminating it ("Ditolak").
  const handleReturnForRevision = () => {
    if (!reviseReason.trim()) {
      toast.error('Alasan revisi wajib diisi.');
      return;
    }
    if (aiReportData) saveAiReport(report.id, aiReportData, false);
    nationalRevise(report.id, reviseReason);
    setReviseOpen(false);
    setReviseReason('');
    toast.success('Laporan dikembalikan untuk revisi.');
  };
  // Workflow rail state derived from report.stage
  const STAGE_ORDER = ['Draft', 'Dikirim', 'Review Koordinator', 'Review BINDA', 'Diverifikasi', 'Analisis', 'Disetujui BINDA', 'Review Nasional', 'Diterima Nasional', 'Tindak Lanjut', 'Selesai'];
  const stageIdx = STAGE_ORDER.indexOf(report.stage || 'Draft');
  const reached = (stage: string) => stageIdx >= STAGE_ORDER.indexOf(stage);
  const canVerifyBinda = role === 'Analis' || role === 'Super Admin Nasional';
  const canApproveKabinda = role === 'Kepala BINDA' || role === 'Wakil Kepala BINDA' || role === 'Super Admin Nasional';
  const canNational = role === 'Administrator Nasional' || role === 'Super Admin Nasional' || role === 'Analis';
  const workflowNodes = [{
    key: 'field',
    title: 'PERSONEL LAPANGAN',
    sub: 'Bahan Keterangan',
    icon: User,
    done: true,
    action: null as null | (() => void),
    actionLabel: ''
  }, {
    key: 'binda',
    title: 'ANALIS BINDA',
    sub: 'Laporan Intelijen BINDA',
    icon: FileSearch,
    done: reached('Diverifikasi'),
    current: report.stage === 'Dikirim' || report.stage === 'Review BINDA',
    action: canVerifyBinda && aiReportData && !reached('Diverifikasi') ? handleVerifyBinda : null,
    actionLabel: 'Verifikasi BINDA'
  }, {
    key: 'kabinda',
    title: 'KABINDA',
    sub: 'Persetujuan',
    icon: ShieldCheck,
    done: reached('Disetujui BINDA'),
    current: report.stage === 'Diverifikasi' || report.stage === 'Analisis',
    action: canApproveKabinda && aiReportData && !reached('Disetujui BINDA') && reached('Diverifikasi') ? handleApproveKabinda : null,
    actionLabel: 'Setujui BINDA'
  }, {
    key: 'national',
    title: 'ANALIS NASIONAL',
    sub: 'Laporan Intelijen Nasional',
    icon: Globe,
    done: reached('Diterima Nasional'),
    current: report.stage === 'Disetujui BINDA' || report.stage === 'Review Nasional',
    action: canNational && report.stage === 'Disetujui BINDA' ? () => setNationalOpen(true) : null,
    actionLabel: 'Review Nasional'
  }, {
    key: 'dashboard',
    title: 'DASHBOARD NASIONAL',
    sub: 'Distribusi',
    icon: Target,
    done: reached('Diterima Nasional'),
    action: reached('Diterima Nasional') ? () => navigate(`/reports/${report.id}`) : null,
    actionLabel: 'Lihat'
  }, {
    key: 'command',
    title: 'COMMAND CENTER',
    sub: 'Operasional',
    icon: Radio,
    done: reached('Diterima Nasional'),
    action: reached('Diterima Nasional') ? () => navigate('/command-center') : null,
    actionLabel: 'Buka'
  }, {
    key: 'kpi',
    title: 'KPI ENGINE',
    sub: 'Dampak Kinerja',
    icon: Gauge,
    done: reached('Diterima Nasional'),
    action: reached('Diterima Nasional') ? () => navigate('/kpi') : null,
    actionLabel: 'Buka'
  }];
  return <div className="min-h-full flex flex-col animate-in fade-in duration-500" data-id="element-3148">
      <div className="flex items-center justify-between border-b border-border/50 pb-4 mb-4 shrink-0" data-id="element-3149">
        <div className="flex items-center gap-4" data-id="element-3150">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/reports/${report.id}`)} className="hover:bg-primary/20 hover:text-primary" data-id="element-3151">
            <ArrowLeft className="w-5 h-5" data-id="element-3152" />
          </Button>
          <div data-id="element-3153">
            <h1 className="text-xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3 uppercase" data-id="element-3154">
              Intelligence Report Studio
            </h1>
            <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-3155">
              DENS CAKRA // Format Standar Nasional
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3156">
          <StatusBadge status={report.status} data-id="element-3157" />
          <UrgencyBadge urgency={report.urgency} data-id="element-3158" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-[calc(100vh-13rem)]" data-id="element-3159">
        {/* PANEL KIRI: BAHAN KETERANGAN */}
        <Card className="lg:col-span-3 glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-3160">
          <CardHeader className="p-3 border-b border-border/50 bg-card/50 shrink-0" data-id="element-3161">
            <CardTitle className="text-xs font-mono uppercase tracking-widest flex items-center gap-2 text-muted-foreground" data-id="element-3162">
              <FileSearch className="w-4 h-4" data-id="element-3163" /> Bahan Keterangan
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar" data-id="element-3164">
            <div className="p-4 space-y-4" data-id="element-3165">
              <div className="bg-black/30 p-3 rounded border border-border/30 space-y-2" data-id="element-3166">
                <div className="flex justify-between items-center" data-id="element-3167">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3168">
                    Nomor Laporan
                  </span>
                  <span className="text-xs font-mono text-primary" data-id="element-3169">
                    {report.id}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-3170">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3171">
                    Pengirim
                  </span>
                  <span className="text-xs font-mono" data-id="element-3172">{report.sender}</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-3173">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3174">
                    BINDA
                  </span>
                  <span className="text-xs font-mono" data-id="element-3175">
                    {report.binda || report.region}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-3176">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3177">
                    Wilayah
                  </span>
                  <span className="text-xs font-mono" data-id="element-3178">{report.region}</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-3179">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3180">
                    Tanggal
                  </span>
                  <span className="text-xs font-mono" data-id="element-3181">
                    {(report.time || '').split(' ')[0] || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-3182">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3183">
                    Jam
                  </span>
                  <span className="text-xs font-mono" data-id="element-3184">
                    {(report.time || '').split(' ')[1] || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-3185">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3186">
                    Kategori
                  </span>
                  <span className="text-xs font-mono" data-id="element-3187">{report.category}</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-3188">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase" data-id="element-3189">
                    Tingkat Urgensi
                  </span>
                  <span className="text-xs font-mono" data-id="element-3190">{report.urgency}</span>
                </div>
              </div>

              <div data-id="element-3191">
                <h4 className="text-[10px] font-mono uppercase text-muted-foreground mb-2" data-id="element-3192">
                  Bagian Narasi
                </h4>
                <div className="space-y-2" data-id="element-3193">
                  <div className="bg-black/30 p-3 rounded border border-border/30 text-xs text-foreground/90 leading-relaxed font-mono whitespace-pre-wrap" data-id="element-3194">
                    <span className="text-[10px] text-primary block mb-1" data-id="element-3195">
                      Observasi / Keterangan Lapangan:
                    </span>
                    {report.ringkasan || report.description || 'Tidak ada narasi.'}
                  </div>
                  {report.kronologi && <div className="bg-black/30 p-3 rounded border border-border/30 text-xs text-foreground/90 leading-relaxed font-mono whitespace-pre-wrap" data-id="element-3196">
                      <span className="text-[10px] text-primary block mb-1" data-id="element-3197">
                        Kronologi:
                      </span>
                      {report.kronologi}
                    </div>}
                  {report.pihakTerkait && <div className="bg-black/30 p-3 rounded border border-border/30 text-xs text-foreground/90 leading-relaxed font-mono whitespace-pre-wrap" data-id="element-3198">
                      <span className="text-[10px] text-primary block mb-1" data-id="element-3199">
                        Pihak Terkait:
                      </span>
                      {report.pihakTerkait}
                    </div>}
                  {report.potensiDampak && <div className="bg-black/30 p-3 rounded border border-border/30 text-xs text-foreground/90 leading-relaxed font-mono whitespace-pre-wrap" data-id="element-3200">
                      <span className="text-[10px] text-primary block mb-1" data-id="element-3201">
                        Potensi Dampak Awal:
                      </span>
                      {report.potensiDampak}
                    </div>}
                </div>
              </div>

              <div data-id="element-3202">
                <h4 className="text-[10px] font-mono uppercase text-muted-foreground mb-2" data-id="element-3203">
                  Bagian Lampiran
                </h4>
                <div className="grid grid-cols-2 gap-2" data-id="element-3204">
                  {report.attachments && report.attachments.length > 0 ? report.attachments.map((att, i) => <div key={i} onClick={() => setPreviewAtt(att)} className="bg-black/40 border border-border/50 rounded p-2 flex flex-col items-center justify-center gap-1 text-center cursor-pointer hover:border-primary/50 transition-colors" data-id="element-3205">
                        {att.kind === 'foto' ? <FileImage className="w-4 h-4 text-muted-foreground" data-id="element-3206" /> : att.kind === 'video' ? <FileVideo className="w-4 h-4 text-muted-foreground" data-id="element-3207" /> : att.kind === 'audio' ? <FileAudio className="w-4 h-4 text-muted-foreground" data-id="element-3208" /> : <FileText className="w-4 h-4 text-muted-foreground" data-id="element-3209" />}
                        <span className="text-[9px] font-mono text-muted-foreground truncate w-full" data-id="element-3210">
                          {att.name}
                        </span>
                      </div>) : <div className="col-span-2 text-[10px] font-mono text-muted-foreground italic text-center p-2 border border-border/30 rounded bg-black/20" data-id="element-3211">
                      Tidak ada lampiran
                    </div>}
                </div>
              </div>

              {report.geotag && <div data-id="element-3212">
                  <h4 className="text-[10px] font-mono uppercase text-muted-foreground mb-2" data-id="element-3213">
                    Bagian Lokasi
                  </h4>
                  <div className="h-40 rounded border border-border/50 overflow-hidden relative z-0" data-id="element-3214">
                    <MapContainer center={[report.geotag.lat, report.geotag.lng]} zoom={13} style={{
                  height: '100%',
                  width: '100%',
                  background: '#0a0a0a'
                }} zoomControl={false} data-id="element-3215">
                      <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="&copy; CARTO" data-id="element-3216" />
                      <CircleMarker center={[report.geotag.lat, report.geotag.lng]} radius={8} fillColor="var(--destructive)" color="var(--destructive)" weight={2} fillOpacity={0.7} data-id="element-3217">
                        <Popup data-id="element-3218">
                          <div className="text-xs font-mono" data-id="element-3219">
                            {report.geotag.label}
                          </div>
                        </Popup>
                      </CircleMarker>
                    </MapContainer>
                  </div>
                  <div className="mt-2 space-y-1" data-id="element-3220">
                    <div className="text-[9px] font-mono text-muted-foreground flex items-center gap-1" data-id="element-3221">
                      <MapPin className="w-3 h-3 text-primary" data-id="element-3222" /> Koordinat:{' '}
                      {report.geotag.lat}, {report.geotag.lng}
                    </div>
                    <div className="text-[9px] font-mono text-muted-foreground flex items-center gap-1" data-id="element-3223">
                      <MapPin className="w-3 h-3 text-primary" data-id="element-3224" /> Alamat:{' '}
                      {report.geotag.label}
                    </div>
                  </div>
                </div>}
            </div>
          </CardContent>
        </Card>

        {/* PANEL TENGAH: AI ANALYSIS ENGINE */}
        <Card className="lg:col-span-4 glass-panel border-primary/30 flex flex-col overflow-hidden shadow-[0_0_15px_rgba(var(--primary),0.1)]" data-id="element-3225">
          <CardHeader className="p-3 border-b border-primary/30 bg-primary/5 shrink-0" data-id="element-3226">
            <CardTitle className="text-xs font-mono uppercase tracking-widest flex items-center gap-2 text-primary" data-id="element-3227">
              <Cpu className="w-4 h-4" data-id="element-3228" /> AI Analysis Engine
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 flex flex-col min-h-0" data-id="element-3229">
            <div className="p-3 border-b border-border/50 bg-black/20 grid grid-cols-2 gap-2 shrink-0" data-id="element-3230">
              <Button size="sm" onClick={handleGenerateFull} disabled={isGenerating} className="col-span-2 text-[10px] font-mono h-8 bg-primary text-primary-foreground" data-id="element-3231">
                {isGenerating ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" data-id="element-3232" /> : <Cpu className="w-3 h-3 mr-1" data-id="element-3233" />}
                Generate Laporan Intelijen
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerateSection('fakta')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3234">
                Generate Fakta-Fakta
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerateSection('analisis')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3235">
                Generate Analisis
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerateSection('dampak')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3236">
                Generate Potensi Dampak
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerateSection('perkembangan')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3237">
                Generate Perkiraan Perkembangan
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerateSection('rekomendasi')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3238">
                Generate Rekomendasi
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerateSection('informasi')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3239">
                Generate Laporan Informasi
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerateSection('executive')} disabled={isGenerating} className="col-span-2 text-[9px] font-mono h-7" data-id="element-3240">
                Generate Executive Summary
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-4" data-id="element-3241">
              {!aiReportData && !isGenerating ? <div className="h-full flex flex-col items-center justify-center text-center space-y-3 opacity-50" data-id="element-3242">
                  <Cpu className="w-12 h-12 text-muted-foreground" data-id="element-3243" />
                  <p className="text-xs font-mono text-muted-foreground max-w-[200px]" data-id="element-3244">
                    Sistem AI siap menyusun draft laporan intelijen.
                  </p>
                </div> : isGenerating ? <div className="h-full flex flex-col items-center justify-center text-center space-y-4" data-id="element-3245">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin" data-id="element-3246" />
                  <p className="text-xs font-mono text-primary uppercase tracking-widest animate-pulse" data-id="element-3247">
                    Menganalisis Data...
                  </p>
                </div> : <div className="space-y-4 animate-in fade-in" data-id="element-3248">
                  <div className="mb-4 bg-black/40 p-2 rounded border border-border/50" data-id="element-3249">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-3250">
                      <Button size="sm" variant="ghost" onClick={handleAddParagraph} className="h-6 text-[9px] font-mono px-2" data-id="element-3251">
                        <PlusCircle className="w-3 h-3 mr-1" data-id="element-3252" /> Tambah Paragraf
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleDeleteParagraph} className="h-6 text-[9px] font-mono px-2" data-id="element-3253">
                        <Trash2 className="w-3 h-3 mr-1" data-id="element-3254" /> Hapus Paragraf
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleHighlight} className="h-6 text-[9px] font-mono px-2" data-id="element-3255">
                        <Highlighter className="w-3 h-3 mr-1" data-id="element-3256" /> Highlight
                      </Button>
                    </div>
                    <p className="text-[9px] font-mono text-muted-foreground mt-1.5 px-1" data-id="element-3257">
                      Bagian aktif:{' '}
                      <span className="text-primary" data-id="element-3258">
                        {FIELD_LABEL[focusedField]}
                      </span>{' '}
                      — klik bagian yang ingin disunting lalu gunakan tombol di
                      atas.
                    </p>
                  </div>

                  <div className="space-y-1" data-id="element-3259">
                    <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3260">
                      Klasifikasi
                    </label>
                    <select className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:outline-none focus:border-primary/50 text-destructive font-bold" value={aiReportData?.classification || 'RAHASIA'} onChange={e => handleReportChange('classification', e.target.value)} data-id="element-3261">
                      <option value="RAHASIA" data-id="element-3262">RAHASIA</option>
                      <option value="TERBATAS" data-id="element-3263">TERBATAS</option>
                      <option value="SANGAT RAHASIA" data-id="element-3264">SANGAT RAHASIA</option>
                    </select>
                  </div>

                  <div className="space-y-1" data-id="element-3265">
                    <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3266">
                      I. Fakta-Fakta
                    </label>
                    <textarea className="w-full bg-black/20 border border-border/30 focus:border-primary/50 rounded p-2 text-xs leading-relaxed focus:outline-none min-h-[100px] resize-y font-mono" value={aiReportData?.facts || ''} onFocus={() => setFocusedField('facts')} onChange={e => handleReportChange('facts', e.target.value)} data-id="element-3267" />
                  </div>

                  <div className="space-y-1" data-id="element-3268">
                    <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3269">
                      II. Analisis
                    </label>
                    <textarea className="w-full bg-black/20 border border-border/30 focus:border-primary/50 rounded p-2 text-xs leading-relaxed focus:outline-none min-h-[100px] resize-y font-mono" value={aiReportData?.assessment || ''} onFocus={() => setFocusedField('assessment')} onChange={e => handleReportChange('assessment', e.target.value)} data-id="element-3270" />
                  </div>

                  <div className="space-y-1" data-id="element-3271">
                    <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3272">
                      III. Potensi Dampak
                    </label>
                    <textarea className="w-full bg-black/20 border border-border/30 focus:border-primary/50 rounded p-2 text-xs leading-relaxed focus:outline-none min-h-[80px] resize-y font-mono" value={aiReportData?.potentialImpact || ''} onFocus={() => setFocusedField('potentialImpact')} onChange={e => handleReportChange('potentialImpact', e.target.value)} data-id="element-3273" />
                  </div>

                  <div className="space-y-1" data-id="element-3274">
                    <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3275">
                      IV. Perkiraan Perkembangan
                    </label>
                    <textarea className="w-full bg-black/20 border border-border/30 focus:border-primary/50 rounded p-2 text-xs leading-relaxed focus:outline-none min-h-[80px] resize-y font-mono" value={Array.isArray(aiReportData?.earlyWarning) ? aiReportData.earlyWarning.join('\n\n') : aiReportData?.earlyWarning || ''} onFocus={() => setFocusedField('earlyWarning')} onChange={e => handleReportChange('earlyWarning', e.target.value.split('\n\n').map(s => s.trim()).filter(Boolean) as any)} data-id="element-3276" />
                  </div>

                  <div className="space-y-1" data-id="element-3277">
                    <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3278">
                      V. Rekomendasi
                    </label>
                    <textarea className="w-full bg-black/20 border border-border/30 focus:border-primary/50 rounded p-2 text-xs leading-relaxed focus:outline-none min-h-[80px] resize-y font-mono" value={aiReportData?.recommendations || ''} onFocus={() => setFocusedField('recommendations')} onChange={e => handleReportChange('recommendations', e.target.value)} data-id="element-3279" />
                  </div>
                </div>}
            </div>
          </CardContent>
        </Card>

        {/* PANEL KANAN: DRAFT LAPORAN RESMI */}
        <Card className="lg:col-span-5 glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-3280">
          <CardHeader className="p-3 border-b border-border/50 bg-card/50 shrink-0 flex flex-row justify-between items-center" data-id="element-3281">
            <CardTitle className="text-xs font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3282">
              <FileText className="w-4 h-4 text-primary" data-id="element-3283" /> Draft Laporan Resmi
            </CardTitle>
            <div className="flex items-center gap-2" data-id="element-3284">
              <Button size="sm" variant="outline" disabled={!aiReportData} onClick={() => setNoteOpen(true)} className="h-7 text-[10px] font-mono" data-id="element-3285">
                <StickyNote className="w-3 h-3 mr-1" data-id="element-3286" /> Catatan
              </Button>
              <Button size="sm" variant="outline" disabled={revisions.length === 0} onClick={() => setCompareOpen(true)} className="h-7 text-[10px] font-mono" data-id="element-3287">
                <GitCompare className="w-3 h-3 mr-1" data-id="element-3288" /> Riwayat Revisi (
                {revisions.length})
              </Button>
              <Button size="sm" variant="outline" disabled={!aiReportData} onClick={handleSaveDraft} className="h-7 text-[10px] font-mono" data-id="element-3289">
                <Save className="w-3 h-3 mr-1" data-id="element-3290" /> Simpan Draft
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar bg-black/40" data-id="element-3291">
            {!aiReportData ? <div className="h-full flex items-center justify-center text-center p-6 opacity-50" data-id="element-3292">
                <p className="text-xs font-mono text-muted-foreground" data-id="element-3293">
                  Preview real-time akan muncul di sini.
                </p>
              </div> : <div className="p-6" data-id="element-3294">
                <IntelReportPreview data={aiReportData} reportId={report.id} region={report.region} sender={report.sender} data-id="element-3295" />
              </div>}
          </CardContent>

          {/* Validasi Berjenjang Controls */}
          {aiReportData && <div className="p-3 border-t border-border/50 bg-card/50 shrink-0 flex items-center justify-between gap-2" data-id="element-3296">
              <div className="flex gap-2" data-id="element-3297">
                <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)} className="text-[10px] font-mono border-destructive/50 text-destructive hover:bg-destructive/10" data-id="element-3298">
                  <XCircle className="w-3 h-3 mr-1" data-id="element-3299" /> Reject
                </Button>
                <Button size="sm" variant="outline" onClick={() => setReviseOpen(true)} className="text-[10px] font-mono border-orange-500/50 text-orange-500 hover:bg-orange-500/10" data-id="element-3300">
                  <CornerUpLeft className="w-3 h-3 mr-1" data-id="element-3301" /> Return for Revision
                </Button>
              </div>
              <div className="flex gap-2" data-id="element-3302">
                {canVerifyBinda && !reached('Diverifikasi') && <Button size="sm" onClick={handleVerifyBinda} className="text-[10px] font-mono bg-[var(--status-active)] text-black hover:bg-[var(--status-active)]/80" data-id="element-3303">
                    <CheckSquare className="w-3 h-3 mr-1" data-id="element-3304" /> Verifikasi BINDA
                  </Button>}
                {canApproveKabinda && reached('Diverifikasi') && !reached('Disetujui BINDA') && <Button size="sm" onClick={handleApproveKabinda} className="text-[10px] font-mono bg-primary text-primary-foreground" data-id="element-3305">
                      <ShieldCheck className="w-3 h-3 mr-1" data-id="element-3306" /> Setujui BINDA
                    </Button>}
                {canNational && reached('Disetujui BINDA') && !reached('Diterima Nasional') && <Button size="sm" onClick={() => setNationalOpen(true)} className="text-[10px] font-mono bg-blue-500 text-white hover:bg-blue-600" data-id="element-3307">
                      <Globe className="w-3 h-3 mr-1" data-id="element-3308" /> Review Nasional
                    </Button>}
              </div>
            </div>}
        </Card>
      </div>

      {/* WORKFLOW RAIL */}
      <Card className="glass-panel border-border/50 mt-4 shrink-0" data-id="element-3309">
        <CardHeader className="p-3 border-b border-border/50 bg-card/50" data-id="element-3310">
          <CardTitle className="text-xs font-mono uppercase tracking-widest flex items-center gap-2 text-muted-foreground" data-id="element-3311">
            <Send className="w-4 h-4 text-primary" data-id="element-3312" /> Alur Validasi & Distribusi
            Nasional
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 overflow-x-auto" data-id="element-3313">
          <div className="flex items-stretch gap-2 min-w-max" data-id="element-3314">
            {workflowNodes.map((node, i) => {
            const Icon = node.icon;
            const isActive = node.action !== null;
            return <Fragment key={node.key} data-id="element-3315">
                  <div className={`flex flex-col w-44 rounded border p-3 transition-colors ${node.done ? 'border-[var(--status-active)]/40 bg-[var(--status-active)]/5' : isActive ? 'border-primary/50 bg-primary/5 shadow-[0_0_12px_rgba(var(--primary),0.15)]' : 'border-border/30 bg-black/20 opacity-70'}`} data-id="element-3316">
                    <div className="flex items-center justify-between mb-2" data-id="element-3317">
                      <Icon className={`w-4 h-4 ${node.done ? 'text-[var(--status-active)]' : isActive ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-3318" />
                      {node.done && <CheckCircle className="w-3.5 h-3.5 text-[var(--status-active)]" data-id="element-3319" />}
                    </div>
                    <div className="text-[10px] font-mono font-bold uppercase tracking-wider leading-tight" data-id="element-3320">
                      {node.title}
                    </div>
                    <div className="text-[9px] font-mono text-muted-foreground mt-0.5 leading-tight" data-id="element-3321">
                      {node.sub}
                    </div>
                    {node.action ? <Button size="sm" onClick={node.action} className="mt-3 h-7 text-[9px] font-mono bg-primary/20 text-primary border border-primary/50 hover:bg-primary hover:text-primary-foreground" data-id="element-3322">
                        {node.actionLabel}
                      </Button> : <div className="mt-3 h-7 flex items-center text-[9px] font-mono text-muted-foreground/60 uppercase tracking-wider" data-id="element-3323">
                        {node.done ? 'Selesai' : 'Menunggu'}
                      </div>}
                  </div>
                  {i < workflowNodes.length - 1 && <div className="flex items-center text-muted-foreground/40 shrink-0" data-id="element-3324">
                      →
                    </div>}
                </Fragment>;
          })}
          </div>
        </CardContent>
      </Card>

      {/* DIALOG: Tambah Catatan */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen} data-id="element-3325">
        <DialogContent className="glass-panel-heavy border-border/50" data-id="element-3326">
          <DialogHeader data-id="element-3327">
            <DialogTitle className="font-mono uppercase tracking-widest" data-id="element-3328">
              Tambah Catatan Analis
            </DialogTitle>
            <DialogDescription className="font-mono text-xs" data-id="element-3329">
              Catatan akan dilampirkan pada bagian Analisis laporan.
            </DialogDescription>
          </DialogHeader>
          <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[100px]" placeholder="Tulis catatan analis..." value={noteText} onChange={e => setNoteText(e.target.value)} data-id="element-3330" />
          <DialogFooter data-id="element-3331">
            <Button variant="outline" onClick={() => setNoteOpen(false)} className="font-mono text-xs" data-id="element-3332">
              Batal
            </Button>
            <Button onClick={handleAddNote} className="font-mono text-xs bg-primary text-primary-foreground" data-id="element-3333">
              Tambahkan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Bandingkan Revisi */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen} data-id="element-3334">
        <DialogContent className="glass-panel-heavy border-border/50 max-w-3xl max-h-[85vh] overflow-y-auto custom-scrollbar" data-id="element-3335">
          <DialogHeader data-id="element-3336">
            <DialogTitle className="font-mono uppercase tracking-widest" data-id="element-3337">
              Riwayat Revisi
            </DialogTitle>
            <DialogDescription className="font-mono text-xs" data-id="element-3338">
              Riwayat snapshot draf yang tersimpan dalam sesi ini.
            </DialogDescription>
          </DialogHeader>
          {revisions.length === 0 ? <div className="text-xs font-mono text-muted-foreground italic py-6 text-center" data-id="element-3339">
              Belum ada revisi tersimpan.
            </div> : <div className="space-y-3 py-2" data-id="element-3340">
              {revisions.map((rev, i) => {
            const prev = revisions[i + 1];
            return <div key={i} className="border border-border/40 rounded p-3 bg-black/20" data-id="element-3341">
                    <div className="flex items-center justify-between mb-2" data-id="element-3342">
                      <span className="text-xs font-mono text-primary font-bold" data-id="element-3343">
                        {rev.label}
                      </span>
                      <span className="text-[10px] font-mono text-muted-foreground" data-id="element-3344">
                        {rev.ts}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[10px] font-mono" data-id="element-3345">
                      <div data-id="element-3346">
                        <div className="text-muted-foreground uppercase mb-1" data-id="element-3347">
                          Fakta-Fakta
                        </div>
                        <div className="text-foreground/80 line-clamp-4 whitespace-pre-wrap" data-id="element-3348">
                          {rev.data.facts}
                        </div>
                      </div>
                      <div data-id="element-3349">
                        <div className="text-muted-foreground uppercase mb-1" data-id="element-3350">
                          Rekomendasi
                        </div>
                        <div className="text-foreground/80 line-clamp-4 whitespace-pre-wrap" data-id="element-3351">
                          {rev.data.recommendations}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3" data-id="element-3352">
                      {prev && <span className="text-[9px] font-mono text-muted-foreground/70" data-id="element-3353">
                          {rev.data.facts !== prev.data.facts || rev.data.recommendations !== prev.data.recommendations ? '● Ada perubahan dari revisi sebelumnya' : '○ Tidak ada perubahan teks utama'}
                        </span>}
                      <Button size="sm" variant="outline" onClick={() => {
                  setAiReportData({
                    ...rev.data
                  });
                  setCompareOpen(false);
                  toast.success(`Memuat ${rev.label} ke editor.`);
                }} className="h-6 text-[9px] font-mono ml-auto" data-id="element-3354">
                        Pulihkan Versi Ini
                      </Button>
                    </div>
                  </div>;
          })}
            </div>}
        </DialogContent>
      </Dialog>

      {/* DIALOG: Review Nasional */}
      <Dialog open={nationalOpen} onOpenChange={setNationalOpen} data-id="element-3355">
        <DialogContent className="glass-panel-heavy border-border/50 max-w-xl max-h-[85vh] overflow-y-auto custom-scrollbar" data-id="element-3356">
          <DialogHeader data-id="element-3357">
            <DialogTitle className="font-mono uppercase tracking-widest" data-id="element-3358">
              Review Analis Nasional / Annev Pusat
            </DialogTitle>
            <DialogDescription className="font-mono text-xs" data-id="element-3359">
              Validasi akhir untuk menjadi Laporan Intelijen Nasional.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2" data-id="element-3360">
            <Button variant="outline" size="sm" className="w-full text-[10px] font-mono border-primary/50 text-primary hover:bg-primary/10" onClick={() => {
            setExecSummary(`RINGKASAN EKSEKUTIF NASIONAL:\n\nBerdasarkan laporan intelijen dari ${report.region} terkait ${report.category}, situasi dinilai memiliki tingkat ancaman ${report.urgency}. Langkah mitigasi telah diinstruksikan kepada unit operasional terkait, dengan pemantauan ketat 48 jam ke depan.`);
            setNationalNote('Ancaman terkonfirmasi valid dan memerlukan atensi nasional.');
            toast.success('Draft Nasional di-generate oleh AI.');
          }} data-id="element-3361">
              <Cpu className="w-3 h-3 mr-2" data-id="element-3362" /> Generate National Assessment (AI)
            </Button>
            <div className="space-y-1" data-id="element-3363">
              <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3364">
                Executive Summary (Wajib)
              </label>
              <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[100px]" value={execSummary} onChange={e => setExecSummary(e.target.value)} data-id="element-3365" />
            </div>
            <div className="space-y-1" data-id="element-3366">
              <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3367">
                Catatan / National Assessment
              </label>
              <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[60px]" value={nationalNote} onChange={e => setNationalNote(e.target.value)} data-id="element-3368" />
            </div>
            <div className="space-y-1" data-id="element-3369">
              <label className="text-[10px] font-mono uppercase text-destructive" data-id="element-3370">
                Alasan Revisi (jika dikembalikan ke BINDA)
              </label>
              <textarea className="w-full bg-black/40 border border-destructive/30 rounded p-3 text-xs font-mono focus:outline-none focus:border-destructive/50 min-h-[60px]" value={reviseReason} onChange={e => setReviseReason(e.target.value)} data-id="element-3371" />
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between" data-id="element-3372">
            <Button variant="outline" onClick={handleNationalRevise} className="font-mono text-xs border-destructive/50 text-destructive hover:bg-destructive/10" data-id="element-3373">
              Revisi ke BINDA
            </Button>
            <Button onClick={handleNationalAccept} className="font-mono text-xs bg-primary text-primary-foreground" data-id="element-3374">
              <Globe className="w-3 h-3 mr-1" data-id="element-3375" /> Terima Nasional
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Reject / Return */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen} data-id="element-3376">
        <DialogContent className="glass-panel-heavy border-border/50" data-id="element-3377">
          <DialogHeader data-id="element-3378">
            <DialogTitle className="font-mono uppercase tracking-widest text-destructive" data-id="element-3379">
              Tolak Laporan
            </DialogTitle>
            <DialogDescription className="font-mono text-xs" data-id="element-3380">
              Laporan akan ditandai DITOLAK dan keluar dari alur produksi.
              Berikan alasan penolakan.
            </DialogDescription>
          </DialogHeader>
          <textarea className="w-full bg-black/40 border border-destructive/30 rounded p-3 text-xs font-mono focus:outline-none focus:border-destructive/50 min-h-[100px]" placeholder="Alasan penolakan..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-id="element-3381" />
          <DialogFooter data-id="element-3382">
            <Button variant="outline" onClick={() => setRejectOpen(false)} className="font-mono text-xs" data-id="element-3383">
              Batal
            </Button>
            <Button onClick={handleReject} className="font-mono text-xs bg-destructive text-destructive-foreground" data-id="element-3384">
              Kirim Penolakan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Return for Revision */}
      <Dialog open={reviseOpen} onOpenChange={setReviseOpen} data-id="element-3385">
        <DialogContent className="glass-panel-heavy border-border/50" data-id="element-3386">
          <DialogHeader data-id="element-3387">
            <DialogTitle className="font-mono uppercase tracking-widest text-orange-500" data-id="element-3388">
              Kembalikan untuk Revisi
            </DialogTitle>
            <DialogDescription className="font-mono text-xs" data-id="element-3389">
              Laporan dikembalikan dengan status PERLU REVISI agar dapat
              disunting ulang. Sertakan catatan revisi.
            </DialogDescription>
          </DialogHeader>
          <textarea className="w-full bg-black/40 border border-orange-500/30 rounded p-3 text-xs font-mono focus:outline-none focus:border-orange-500/50 min-h-[100px]" placeholder="Catatan / alasan revisi..." value={reviseReason} onChange={e => setReviseReason(e.target.value)} data-id="element-3390" />
          <DialogFooter data-id="element-3391">
            <Button variant="outline" onClick={() => setReviseOpen(false)} className="font-mono text-xs" data-id="element-3392">
              Batal
            </Button>
            <Button onClick={handleReturnForRevision} className="font-mono text-xs bg-orange-500 text-white hover:bg-orange-600" data-id="element-3393">
              <CornerUpLeft className="w-3 h-3 mr-1" data-id="element-3394" /> Kembalikan untuk Revisi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Preview Lampiran */}
      <Dialog open={previewAtt !== null} onOpenChange={o => !o && setPreviewAtt(null)} data-id="element-3395">
        <DialogContent className="glass-panel-heavy border-border/50 max-w-lg" data-id="element-3396">
          <DialogHeader data-id="element-3397">
            <DialogTitle className="font-mono uppercase tracking-widest text-sm flex items-center gap-2" data-id="element-3398">
              <Eye className="w-4 h-4 text-primary" data-id="element-3399" /> Pratinjau Lampiran
            </DialogTitle>
            <DialogDescription className="font-mono text-xs" data-id="element-3400">
              {previewAtt?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2" data-id="element-3401">
            {previewAtt?.kind === 'foto' ? <img src={`https://picsum.photos/seed/${encodeURIComponent(previewAtt.name)}/640/400`} alt={previewAtt.name} className="w-full rounded border border-border/50" data-id="element-3402" /> : previewAtt?.kind === 'video' ? <div className="aspect-video bg-black rounded border border-border/50 flex items-center justify-center" data-id="element-3403">
                <div className="flex flex-col items-center gap-2 text-muted-foreground" data-id="element-3404">
                  <Play className="w-10 h-10" data-id="element-3405" />
                  <span className="text-[10px] font-mono" data-id="element-3406">
                    Pratinjau Video (simulasi)
                  </span>
                </div>
              </div> : previewAtt?.kind === 'audio' ? <div className="bg-black/40 rounded border border-border/50 p-6 flex flex-col items-center gap-3" data-id="element-3407">
                <FileAudio className="w-10 h-10 text-primary" data-id="element-3408" />
                <div className="w-full h-1.5 bg-border/50 rounded-full overflow-hidden" data-id="element-3409">
                  <div className="h-full w-1/3 bg-primary" data-id="element-3410" />
                </div>
                <span className="text-[10px] font-mono text-muted-foreground" data-id="element-3411">
                  Rekaman Audio (simulasi)
                </span>
              </div> : <div className="bg-white/95 text-slate-800 rounded border border-border/50 p-6 min-h-[200px] text-xs font-mono leading-relaxed" data-id="element-3412">
                <p className="font-bold mb-2 uppercase" data-id="element-3413">{previewAtt?.name}</p>
                <p data-id="element-3414">
                  Dokumen pendukung laporan {report.id}. Berisi catatan
                  lapangan, lampiran administratif, dan data verifikasi yang
                  diserahkan oleh {report.sender}.
                </p>
              </div>}
          </div>
          <DialogFooter data-id="element-3415">
            <Button variant="outline" onClick={() => setPreviewAtt(null)} className="font-mono text-xs" data-id="element-3416">
              <X className="w-3 h-3 mr-1" data-id="element-3417" /> Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}